import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Check, Clock, Heart, HeartCrack, Hourglass, MailCheck, Send, Sparkles, UsersRound } from "lucide-react";
import { AppHero } from "./AppHero";
import { AppStateScreen } from "./AppStateScreen";
import { AppStepCards } from "./AppStepCards";
import { MATCH_HOW_IT_WORKS } from "./constants";
import type { CurrentMatch, MatchingEligibilityReason, MatchingLaunchStatus } from "../../types";
import { getInitialLaunchMemberView } from "../../initialLaunchPresentation";

function formatExpiry(expiresAt?: string) {
  if (!expiresAt) return null;
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function NoMatchView({
  isEligible,
  reasons,
  reasonKeys,
  launchStatus,
  invitationsRemaining,
}: {
  isEligible?: boolean;
  reasons?: string[];
  reasonKeys?: MatchingEligibilityReason[];
  launchStatus?: MatchingLaunchStatus;
  invitationsRemaining?: number;
}) {
  if (isEligible && launchStatus) {
    const view = getInitialLaunchMemberView(launchStatus);
    const progress = Math.min(launchStatus.balancedProgress, launchStatus.foundingProfileTarget);
    const hasInvitation = invitationsRemaining == null || invitationsRemaining > 0;
    return (
      <div className={`match-state-view founding-launch founding-launch--${view.kind}`}>
        <section className="founding-launch-card" aria-labelledby="founding-launch-title">
          <div className="founding-launch-icon" aria-hidden="true">
            {view.kind === "collecting" ? <UsersRound /> : <Sparkles />}
          </div>
          <p className="eyebrow">Founding community</p>
          <h1 id="founding-launch-title">{view.title}</h1>

          {view.showProgress && (
            <div className="founding-progress-block">
              <div className="founding-progress-label">
                <strong>{progress} of {launchStatus.foundingProfileTarget} profiles ready</strong>
                <span>{Math.round(progress / launchStatus.foundingProfileTarget * 100)}%</span>
              </div>
              <div
                className="founding-progress-track"
                role="progressbar"
                aria-label="Founding community progress"
                aria-valuemin={0}
                aria-valuemax={launchStatus.foundingProfileTarget}
                aria-valuenow={progress}
                aria-valuetext={`${progress} of ${launchStatus.foundingProfileTarget} profiles ready`}
              >
                <span style={{ width: `${progress / launchStatus.foundingProfileTarget * 100}%` }} />
              </div>
            </div>
          )}

          <p className="founding-launch-body">{view.body}</p>
          <p className="match-queue-status">
            <Check size={14} strokeWidth={2.5} aria-hidden="true" />
            Profile ready for matching
          </p>

          {view.kind === "collecting" && (
            <div className="founding-invite">
              <Link className={`button ${hasInvitation ? "primary" : "secondary"}`} to="/settings/invites">
                <Send size={17} aria-hidden="true" />
                {hasInvitation ? "Invite a friend" : "View your invites"}
              </Link>
              <p>Inviting someone you trust can help Yara's first matching rounds begin sooner.</p>
              {invitationsRemaining === 0 && (
                <small>You don't have an available invitation right now. You can manage invitations from the invite page.</small>
              )}
            </div>
          )}
        </section>
      </div>
    );
  }

  if (isEligible) {
    return (
      <div className="match-state-view match-state-view--eligible">
        <AppStateScreen
          icon={Hourglass}
          title="You're ready for matching"
          tone="waiting"
          body="We'll let you know when there's a thoughtful introduction for you."
        />
      </div>
    );
  }

  const completionReasons: MatchingEligibilityReason[] = [
    "missing_profile",
    "incomplete_profile",
    "missing_preferences",
    "insufficient_photos",
  ];
  const needsCompletion = reasonKeys?.some((reason) => completionReasons.includes(reason)) ?? true;
  const onlyNeedsPreferences = reasonKeys?.length === 1 && reasonKeys[0] === "missing_preferences";
  const completionHref = onlyNeedsPreferences ? "/settings/edit-preferences" : "/settings/edit-profile";
  const editLabel = onlyNeedsPreferences ? "Edit preferences" : "Edit profile";
  const completionLabel = onlyNeedsPreferences ? "Complete preferences" : "Complete profile";
  if (!needsCompletion && reasonKeys?.includes("unverified_email")) {
    return (
      <div className="match-state-view match-state-view--eligible">
        <AppStateScreen
          icon={MailCheck}
          title="Verify your email"
          tone="neutral"
          body="Confirm your email before Yara includes your profile in matching. You can send a fresh verification link from Account settings."
        >
          <Link className="button primary" to="/settings/account">Open Account settings</Link>
        </AppStateScreen>
      </div>
    );
  }
  if (!needsCompletion && reasonKeys?.includes("active_match")) {
    return (
      <div className="match-state-view match-state-view--eligible">
        <AppStateScreen
          icon={Hourglass}
          title="An introduction is in progress"
          tone="waiting"
          body="Yara is waiting for the current introduction step to finish. We'll let you know when there's something for you to see."
        />
      </div>
    );
  }
  if (!needsCompletion && reasonKeys?.includes("pending_approval")) {
    return (
      <div className="match-state-view match-state-view--eligible">
        <AppStateScreen
          icon={Hourglass}
          title="Your profile is being reviewed"
          tone="waiting"
          body="Your profile is complete. We'll let you know as soon as it's approved for matching."
        />
      </div>
    );
  }
  if (!needsCompletion) {
    return (
      <div className="match-state-view match-state-view--eligible">
        <AppStateScreen
          icon={AlertCircle}
          title={reasonKeys?.includes("skip_period") ? "Matching is paused" : "Matching is unavailable"}
          tone="neutral"
          body={reasons?.join(" ") || "Check your account status before returning to matching."}
        />
      </div>
    );
  }

  return (
    <div className="match-state-view match-state-view--no-match">
      <AppHero
        eyebrow="Matching"
        title="How matching works"
        description="Complete your profile so Yara can introduce you thoughtfully."
        icon={Heart}
      />
      <div className="app-eligibility-banner" role="status">
        <AlertCircle size={18} aria-hidden="true" />
        <div>
          <strong>Profile not ready for matching</strong>
          <p>{reasons?.join(" ") || "Add photos and complete your profile details."}</p>
        </div>
        <Link className="button secondary" to={completionHref} state={{ from: "/app/match" }}>{editLabel}</Link>
      </div>
      <AppStepCards label="Three calm steps" steps={MATCH_HOW_IT_WORKS} />
      <div className="app-state-screen-actions app-state-screen-actions--inline">
        <Link className="button primary" to={completionHref} state={{ from: "/app/match" }}>{completionLabel}</Link>
      </div>
    </div>
  );
}

export function WaitingMatchView({ expiresAt }: { expiresAt?: string }) {
  const expiry = formatExpiry(expiresAt);
  return (
    <AppStateScreen
      icon={Hourglass}
      title="Waiting for their response"
      tone="waiting"
      body={(
        <p>
          You said yes to this introduction. If they say yes too, we will connect you.
          {expiry && <> Response window closes {expiry}.</>}
        </p>
      )}
    />
  );
}

export function RejectedMatchView({ onAcknowledge }: { onAcknowledge: () => void }) {
  return (
    <AppStateScreen
      icon={HeartCrack}
      title="This one did not work out"
      tone="sad"
      body="That is okay. Yara may offer a new introduction in an upcoming round."
    >
      <button className="button primary" type="button" onClick={onAcknowledge}>Got it</button>
    </AppStateScreen>
  );
}

export function PendingMatchHeader({
  expiresAt,
  actions,
}: {
  expiresAt?: string;
  actions: ReactNode;
}) {
  const expiry = formatExpiry(expiresAt);
  return (
    <section className="pending-match-header" aria-labelledby="pending-introduction-title">
      <div className="pending-match-copy">
        <div className="pending-match-meta">
          <span className="pending-match-badge">Private introduction</span>
          {expiry && (
            <span className="pending-match-timer">
              <Clock size={17} aria-hidden="true" />
              <span className="pending-match-timer-copy">
                <span className="pending-match-timer-label">Response deadline</span>
                <strong>{expiry}</strong>
              </span>
            </span>
          )}
        </div>
        <h2 id="pending-introduction-title">Can we make this introduction?</h2>
        <p>Both of you decide privately. If you both say yes, we will connect you.</p>
      </div>
      <div className="pending-match-actions">{actions}</div>
    </section>
  );
}

export type MatchCelebrationProps = {
  match: CurrentMatch;
  messageIndex: number;
};
